import { useCallback } from 'react'
import html2canvas from 'html2canvas'
import { extractPlainTextFromMarkdown } from '../../../utils/markdownUtils'

export const usePreviewActions = () => {
  const handleDownload = useCallback(async (element: HTMLElement, format: 'svg' | 'png' | 'jpeg' = 'png') => {
    const filename = `mermaid-${Date.now()}.${format}`
    
    // 1. 如果是 SVG 格式，依然使用原生的 SVG 序列化下载，保证矢量图的绝对清晰
    if (format === 'svg') {
      const svgElement = element.querySelector('svg')
      if (!svgElement) return

      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      
      // 提取 Mermaid 样式并注入
      const styleSheets = Array.from(document.styleSheets)
      let inlineStyles = ''
      try {
        styleSheets.forEach(sheet => {
          try {
            const rules = Array.from(sheet.cssRules)
            rules.forEach(rule => {
              if (rule.cssText.includes('mermaid')) {
                inlineStyles += rule.cssText
              }
            })
          } catch (e) {}
        })
      } catch (e) {}

      const styleEl = document.createElement('style')
      styleEl.textContent = inlineStyles
      clonedSvg.prepend(styleEl)

      // 尺寸设置
      const width = svgElement.viewBox.baseVal.width || svgElement.clientWidth || 800
      const height = svgElement.viewBox.baseVal.height || svgElement.clientHeight || 600
      clonedSvg.setAttribute('width', width.toString())
      clonedSvg.setAttribute('height', height.toString())

      const svgData = new XMLSerializer().serializeToString(clonedSvg)
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)
      
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = filename
      downloadLink.click()
      URL.revokeObjectURL(url)
      return
    }

    // 2. 如果是 PNG/JPEG，参考项目中“导出文档页面”的成熟方案，改用 html2canvas
    // 这能更好地处理 Mermaid 复杂的 CSS 样式和布局
    try {
      // 优化图片质量：
      // 1. 强制使用更高的 scale (3倍)，不依赖系统的 devicePixelRatio
      // 2. 开启 imageTimeout 确保外部资源加载完成
      // 3. 针对不同格式设置最优导出参数
      const canvas = await html2canvas(element, {
        scale: 3, 
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0, // 禁用超时，确保所有元素加载完成
        ignoreElements: (el) => {
          return el.classList.contains('preview-actions')
        }
      })

      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
      const quality = 1.0 // 提高 JPEG 质量到最高

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          link.click()
          URL.revokeObjectURL(url)
        }
      }, mimeType, quality)
    } catch (err) {
      console.error('Mermaid download failed with html2canvas:', err)
    }
  }, [])

  const handleCopy = useCallback(async (actionButton: HTMLElement, el: HTMLElement) => {
    let textToCopy = ''
    const encodedRaw = el.getAttribute('data-raw-markdown')
    
    if (encodedRaw) {
      try {
        const rawMarkdown = decodeURIComponent(window.atob(encodedRaw))
        textToCopy = extractPlainTextFromMarkdown(rawMarkdown)
      } catch (err) {
        console.error('Failed to decode raw markdown:', err)
      }
    }

    if (!textToCopy) {
      if (el.tagName === 'PRE') {
        textToCopy = el.querySelector('code')?.innerText || el.innerText
      } else if (el.tagName === 'TABLE') {
        const rows = Array.from(el.querySelectorAll('tr'))
        textToCopy = rows.map(row => {
          const cells = Array.from(row.querySelectorAll('th, td'))
          return cells.map(cell => (cell as HTMLElement).innerText.trim()).join('\t')
        }).join('\n')
      }
    }

    try {
      await navigator.clipboard.writeText(textToCopy)
      const originalHtml = actionButton.innerHTML
      actionButton.classList.add('copied')
      actionButton.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `
      setTimeout(() => {
        actionButton.classList.remove('copied')
        actionButton.innerHTML = originalHtml
      }, 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [])

  return { handleDownload, handleCopy }
}
